import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import './modal.css';

interface ModalProps {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: 'schmal' | 'normal' | 'breit' | 'voll';
  /** Zusätzliche Klasse für Sonderfälle — etwa eine feste Fensterhöhe. */
  className?: string;
}

/**
 * Dialog auf Basis von <dialog>: Fokusfalle, Escape und Hintergrundsperre
 * kommen vom Browser statt aus selbstgebautem Tastaturcode.
 */
export function Modal({
  title,
  subtitle,
  onClose,
  children,
  footer,
  width = 'normal',
  className,
}: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog?.open) dialog?.showModal();
    return () => dialog?.close();
  }, []);

  return (
    <dialog
      ref={ref}
      className={`modal modal-${width}${className ? ` ${className}` : ''}`}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        // Klick auf die Abdunklung selbst (nicht auf den Inhalt) schließt.
        if (event.target === ref.current) onClose();
      }}
      aria-labelledby="modal-title"
    >
      <div className="modal-head">
        <div>
          <h2 id="modal-title">{title}</h2>
          {subtitle ? <p className="modal-subtitle">{subtitle}</p> : null}
        </div>
        <button type="button" className="btn btn-ghost btn-icon" onClick={onClose} aria-label="Schließen">
          ✕
        </button>
      </div>
      <div className="modal-body">{children}</div>
      {footer ? <div className="modal-foot">{footer}</div> : null}
    </dialog>
  );
}
